import { UrlWithParsedQuery } from 'url'
import { AppsScripts, PlatformBootstrapData, ViewerAppsUrls } from '@wix/thunderbolt-symbols'
import { extractControllersUrlOverrideMap } from './csmManipulations'
import _ from 'lodash'

type AppsAndScope = { apps: { [appDefinitionId: string]: Array<string> }; scope: 'page' | 'site' }

const getAppsAndScope = (
	appsUrlData: ViewerAppsUrls,
	landingPageId: string,
	platformControllersOnPage?: Record<string, Record<string, Array<string>>>
): AppsAndScope => {
	if (platformControllersOnPage) {
		return {
			// page level script urls
			apps: platformControllersOnPage[landingPageId],
			scope: 'page',
		}
	} else {
		return {
			// site level script urls
			apps: _.mapValues(appsUrlData, (app) => Object.keys(app.widgets || {})),
			scope: 'site',
		}
	}
}

const getAppsScriptsUrls = (
	appsAndScope: AppsAndScope,
	landingPageId: string,
	requestUrl: UrlWithParsedQuery,
	platformBootstrapData: PlatformBootstrapData
) => {
	const getScriptsUrls = (appDefinitionId: string, widgetIds: Array<string> = []) => {
		const appData = platformBootstrapData.appsUrlData[appDefinitionId]
		if (!appData) {
			return []
		}
		const scripts = [appData.viewerScriptUrl]
		if (!platformBootstrapData.appsSpecData[appDefinitionId].isModuleFederated) {
			const controllersOverridesMap = extractControllersUrlOverrideMap(requestUrl)
			_.uniq(widgetIds).forEach((widgetId) => {
				const controllerUrl = appData.widgets && appData.widgets[widgetId]?.controllerUrl
				const overrideUrl = controllersOverridesMap?.[widgetId]
				const url = overrideUrl || controllerUrl
				if (url) {
					scripts.push(url)
				}
			})
		}

		return scripts
	}

	const appsScriptsUrls: { [appDefinitionId: string]: Array<string> } = {}

	_.forEach(appsAndScope.apps, (widgetIds, appDefinitionId) => {
		appsScriptsUrls[appDefinitionId] = getScriptsUrls(appDefinitionId, widgetIds)
	})

	// wixcode app does not appear in platformControllersOnPage
	const { wixCodeAppDefinitionId, wixCodePageIds } = platformBootstrapData.wixCodeBootstrapData
	if (wixCodeAppDefinitionId && wixCodePageIds[landingPageId]) {
		appsScriptsUrls[wixCodeAppDefinitionId] = getScriptsUrls(wixCodeAppDefinitionId)
	}

	return appsScriptsUrls
}

export function getAppsScripts(
	requestUrl: UrlWithParsedQuery,
	platformBootstrapData: PlatformBootstrapData,
	landingPageId?: string,
	platformControllersOnPage?: Record<string, Record<string, Array<string>>>
): AppsScripts {
	if (!landingPageId) {
		return {
			urls: {},
			scope: 'page',
		}
	}

	const appsAndScope = getAppsAndScope(platformBootstrapData.appsUrlData, landingPageId, platformControllersOnPage)

	const appsScriptsUrls = getAppsScriptsUrls(appsAndScope, landingPageId, requestUrl, platformBootstrapData)

	return {
		urls: appsScriptsUrls,
		scope: appsAndScope.scope,
	}
}
